import { useNavigation } from "@react-navigation/native";
import { PartyPopper } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next"; // 👈 Import useTranslation
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { Label } from "../../../ui/Label";
import { Separator } from "../../../ui/Separator";
import { Title } from "../../../ui/Title";
import { EditorRender } from "../../../ui/YooptaEditor";
import type { BusinessPlanModel } from "../BusinessPlanModel";
import { BusinessProfileSettings } from "../tabs/BusinessProfileScreen";

export const BusinessIntro: React.FC<{ businessPlan: BusinessPlanModel }> =
  observer(({ businessPlan }) => {
    const { navigate } = useNavigation();

    // 👈 1. Use Translation Hook and specify the default namespace
    const { t } = useTranslation();

    const user = businessPlan.userInPlan;
    const info = user ? businessPlan.data.userInfo[user.uid] : null;

    const profileIsReady = React.useMemo(() => {
      if (!info) return false;

      // TODO - map up more
      return !!info.profile?.description;
    }, [info]);

    if (!user) {
      return null;
    }

    const hasIntroTextFromCustomer = businessPlan.introTextFromCustomer;

    return (
      <>
        <Box>
          <Title>{t("business:intro_title")}</Title>

          {!!hasIntroTextFromCustomer && (
            <>
              <Label>
                {t("business:from_customer_label", {
                  name: businessPlan.data.customerInfo.name,
                })}
              </Label>
              <EditorRender value={hasIntroTextFromCustomer} />
            </>
          )}
          <Separator />

          <Body>{t("business:intro_instructions")}</Body>
        </Box>

        <BusinessProfileSettings
          user={user}
          businessPlan={businessPlan}
          sectionsEnabled
        />

        <Box>
          <Button
            disabled={!profileIsReady}
            type={"primary"}
            icon={PartyPopper}
            onPress={() => {
              businessPlan.setIntroductionAsCompleted();

              navigate("BusinessHome", {
                businessUid: businessPlan.uid,
              });
            }}
          >
            {profileIsReady
              ? t("business:button_ready")
              : t("business:button_complete_profile")}
          </Button>

          {!profileIsReady && (
            <Button
              type={"ghost"}
              onPress={() => {
                navigate("BusinessHome", {
                  businessUid: businessPlan.uid,
                });
              }}
            >
              {t("business:button_skip")}
            </Button>
          )}
        </Box>
      </>
    );
  });
